import { ExpertDataItem, LogClientStatus, MyMatch } from "@src/api/match";
import { User } from "@src/hook/useUser";
import { MatchStage } from "@src/utils/types/stage";
import moment from "moment";

export enum ExpertDataItemStep {
	"추천" = 1,
	"인터뷰" = 2,
	"제안" = 3,
}

export enum AppliCationStep {
	"LIST",
	"APPLICATION",
	"CONSULTING",
	"TEST",
	"PROPOSAL",
}

export interface ChatExpertItem {
	matchedExpertDataUuid: string;
	matchUuid: string;
	expertUuid: string;
	expert: string;
	client: string;
	job: string;
}

export const getStepArray = (userType: number) =>
	userType === 1 ? ["신청", "추천", "인터뷰", "계약"] : ["추천", "인터뷰", "제안"];

export const steperStyle = {
	"svg.Mui-completed.MuiStepIcon-root": {
		color: "var(--primary)",
	},
	"svg.Mui-active.MuiStepIcon-root": {
		color: "var(--primary)",
	},
	"span.Mui-completed": {
		color: "var(--card-bg) !important",
	},
	"span.Mui-active": {
		color: "var(--card-bg) !important",
	},
	"svg.MuiStepIcon-root": {
		cursor: "pointer",
	},
	"span.MuiStepIcon-root": {
		color: "var(--card-bg)",
	},
	"span.MuiStepLabel-label.Mui-disabled": {
		color: "var(--card-bg)",
	},
};

interface CanEndMatchReturn {
	isShow: boolean;
	canEnd: boolean;
}

export const getAvailableChatMatchedExpert = (matchs: MyMatch[]): ChatExpertItem[] => {
	const availableMatchs = matchs
		.map((match) =>
			match.expertDataList.map((expertData) => {
				const { expertDataList, ...rest } = match;
				return {
					...rest,
					expertData: expertData,
				};
			}),
		)
		.flat();
	return availableMatchs
		.filter((match) => !match.expertData.dateOut)
		.filter(
			(match) =>
				(!match.dateStartMatch &&
					(match.expertData.dateRequestTestSession ||
						match.expertData.dateTestsession ||
						match.expertData.proposalId ||
						match.expertData.dateRequestForProposal)) ||
				(match.dateEndMatch && moment(match.dateEndMatch).isAfter(moment())) ||
				(match.dateStartMatch && !match.dateEndMatch),
		)
		.map((match) => ({
			matchedExpertDataUuid: match.expertData.matchedExpertDataUuid,
			matchUuid: match.matchUuid,
			expertUuid: match.expertData.expertUuid,
			expert: match.expertData.expert,
			client: match.client,
			job: match.expertType,
		}));
};

export const showEndMatch = (matchData: MyMatch) => {
	const { currentLogSettlementDate } = matchData;

	if (!currentLogSettlementDate) {
		return {
			isShow: false,
			text: "",
		};
	}

	const momentDateEndMatch = moment(new Date(currentLogSettlementDate));

	let result = "예상 정산일: ";

	const momentNow = moment();
	let isShow = currentLogSettlementDate ? true : false;

	const year = momentDateEndMatch.year();
	const month = momentDateEndMatch.month() + 1;
	const dateDay = momentDateEndMatch.date();
	if (dateDay <= 15) {
		const momentDate = moment(`${year}-${month}-20`);
		result += momentDate.format("M월 D일 이내");
		if (momentNow.isAfter(momentDate)) {
			isShow = false;
		}
	} else {
		const momentDate = moment(`${year}-${month}-5`).add(1, "month");
		result += momentDate.format("M월 D일 이내");
		if (momentNow.isAfter(momentDate)) {
			isShow = false;
		}
	}

	return {
		isShow,
		text: result,
	};
};

export const canShowEER = (matchData: MyMatch, user: User) => {
	if (user.userType === 1) {
		return false;
	}

	if (matchData.dateEndMatch && matchData.expertEvaluation) {
		const dateEndMatchMoment = moment(new Date(matchData.dateEndMatch)).startOf("day");

		const now = moment();

		if (now.isAfter(dateEndMatchMoment)) {
			return true;
		}
	}

	return false;
};

export const canEndMatch = (matchData: MyMatch, user: User): CanEndMatchReturn => {
	if (user.userType === 2 || matchData.serviceGroup !== "시간제") {
		return {
			isShow: false,
			canEnd: false,
		};
	}

	if (matchData.lastLogStatus !== LogClientStatus.연장) {
		return {
			isShow: false,
			canEnd: false,
		};
	}

	if (matchData.dateStartMatch) {
		const dateStartMatch = moment(new Date(matchData.dateStartMatch)).startOf("day");
		const now = moment();
		if (now.isBefore(dateStartMatch)) {
			return {
				isShow: false,
				canEnd: false,
			};
		}
	}

	if (matchData.dateEndMatch) {
		const dateEndMatch = moment(new Date(matchData.dateEndMatch)).endOf("day");
		const now = moment();
		const diff = dateEndMatch.diff(now, "days");

		if (now.isAfter(dateEndMatch)) {
			return {
				isShow: false,
				canEnd: false,
			};
		} else if (
			matchData.lastLogStartDate &&
			moment().isBefore(moment(matchData.lastLogStartDate))
		) {
			return {
				isShow: true,
				canEnd: false,
			};
		} else if (diff >= 4) {
			return {
				isShow: true,
				canEnd: true,
			};
		} else {
			return {
				isShow: true,
				canEnd: false,
			};
		}
	}

	return {
		isShow: false,
		canEnd: false,
	};
};

export const getMatchesWithSection = (matches?: MyMatch[]) => {
	const ing = matches?.filter((match) => match.stage === "진행" || !match.stage) || [];
	const end = matches?.filter((match) => match.stage === "종료") || [];
	const start = matches?.filter((match) => match.stage === "개시") || [];
	return {
		ing,
		end,
		start,
	};
};

export const getStageDataText = (matchData: MyMatch, user: User) => {
	let step = "신청완료";
	let date = "";
	const expertType = matchData.expertType;
	const expertName = user.userType === 1 ? matchData.expertType : matchData.client;
	const expertNameText = expertName ? expertName : "";
	let clientText = "";
	let cliendDate = "";

	clientText = "서비스 신청이 접수되었어요";
	// console.log("getStageDataText - matchData:", matchData);

	// detailStage가 "신청확정전"이면 무조건 해당 메시지 표시
	if (user.userType === 1 && matchData.detailStage === "신청확정전") {
		step = "신청확정전";
		date = "";
		clientText = "매칭을 최종확정해주세요";
		return { step, date, clientText, cliendDate, expertNameText };
	}

	if (user.userType === 1 && matchData.stageId === MatchStage.신청완료) {
		step = "신청완료";
		date = matchData.dateIntro
			? "사전 미팅 " + moment(new Date(matchData.dateIntro)).format("YY.MM.DD HH:mm")
			: "";
		clientText = "서비스 신청이 접수되었어요";
	}

	if (user.userType === 1 && matchData.dateApplication) {
		step = "매칭 신청서 작성 요청";
		clientText = "매칭 신청서를 작성해주세요";
	}

	if (matchData.dateConsulting && user.userType === 1) {
		const dateConsulting = new Date(matchData.dateConsulting).getTime();
		const now = new Date().getTime();
		if (now < dateConsulting) {
			step = "상담 대기 중";
			date =
				moment(new Date(matchData.dateConsulting)).format("YY년 M월 D일 A h시 mm분") +
				" 예정";

			clientText =
				moment(new Date(matchData.dateConsulting)).format("YY년 M월 D일 A h시 mm분") +
				"\n매칭 컨설팅 진행 예정이에요";
		} else {
			step = "전문가 서칭 중";
			date = "";
			clientText = "적합한 전문가를 서칭중이에요";
		}
	}

	if (user.userType === 2) {
		if (matchData.expertDataList.some((d) => d.dateRecommendToExpert)) {
			step = "인터뷰 지원 요청";
			date = "";
			clientText = "업무요청서를 확인하고 인터뷰에 지원해주세요";
		}

		if (matchData.expertDataList.some((d) => d.dateApplyInterview)) {
			step = "인터뷰 지원 완료";
			date = "";
			clientText = "";
		}

		if (matchData.expertDataList.some((d) => d.dateRecommend)) {
			step = "고객사 검토 중";
			date = "";
			clientText = "고객사가 프로필을 검토중이에요";
		}

		if (matchData.expertDataList.some((d) => d.dateRequestTestSession)) {
			step = "인터뷰 예약 필요";
			date = "";
		}

		if (matchData.expertDataList.some((d) => d.dateRequestConfirmTestsession)) {
			step = "인터뷰 조율 중";
			date = "";
		}

		if (matchData.expertDataList.some((d) => d.dateTestsession)) {
			const dateTestsession = new Date(
				matchData.expertDataList.map((d) => d.dateTestsession)[0],
			).getTime();
			const now = new Date().getTime();
			if (now < dateTestsession) {
				step = "인터뷰 대기 중";
				date =
					moment(
						new Date(matchData.expertDataList.map((d) => d.dateTestsession)[0]),
					).format("YY년 M월 D일 A h시") + " 예정";
			} else {
				step = "전문가 제안 대기 중";
				date = "";
			}
		}

		if (matchData.expertDataList.some((d) => d.proposalId)) {
			step = "전문가 제안서 확인 중";
			date = "";
		}
	}

	if (matchData.expertDataList.length > 0 && user.userType === 1) {
		step = "전문가 추천";
		date = "";
		clientText = "추천 전문가를 확인해보세요";
	}

	if (
		matchData.dateStartMatch &&
		(matchData.isCompleteContract == false || matchData.isCompleteFirstInvoice == false)
	) {
		step = "계약/결제 진행 중";
		date = "";
	}

	if (matchData.dateStartMatch) {
		step = "서비스 시작";
		date = moment(new Date(matchData.dateStartMatch)).format("YY.MM.DD") + " 시작";

		if (matchData.dateEndMatch) {
			cliendDate =
				moment(new Date(matchData.dateEndMatch)).add(1, "days").format("YY.MM.DD") +
				" 서비스 연장 예정";
		}

		clientText = "서비스를 진행중이에요";

		if (!matchData.isCompleteContract || !matchData.isCompleteFirstInvoice) {
			clientText = "계약 및 결제가 진행 중입니다!";
		}
	}

	if (
		matchData.stage == "종료" ||
		((matchData.lastLogStatus === LogClientStatus.보류 ||
			matchData.lastLogStatus === LogClientStatus.이탈) &&
			matchData.dateEndMatch &&
			moment(matchData.dateEndMatch).diff(moment(), "days") < 2 &&
			user.userType === 2) ||
		((matchData.lastLogStatus === LogClientStatus.보류 ||
			matchData.lastLogStatus === LogClientStatus.이탈) &&
			user.userType === 1)
	) {
		step = "서비스 종료";
		date = moment(new Date(matchData.dateEndMatch)).format("YY.MM.DD") + " 종료";
		cliendDate = moment(new Date(matchData.dateEndMatch)).format("YY.MM.DD") + " 서비스 종료";
		clientText = "서비스가 종료되었어요";
	}

	return {
		cliendDate,
		date,
		step,
		expertType,
		expertNameText,
		clientText,
	};
};

// 고객사 관점 스텝
export const getExpertStep = (match: MyMatch, expertDataItem: ExpertDataItem) => {
	let step: ExpertDataItemStep = ExpertDataItemStep.추천;
	let stepDetail = "프로필 검토";
	let isDisabled = false;

	if (expertDataItem.dateRequestTestSession) {
		step = ExpertDataItemStep.인터뷰;
		stepDetail = "일정 제안 예정";
	}

	if (expertDataItem.dateRequestConfirmTestsession) {
		step = ExpertDataItemStep.인터뷰;
		stepDetail = "일정 선택";
	}

	if (expertDataItem.dateOut) {
		step = ExpertDataItemStep.인터뷰;
		stepDetail = "인터뷰 거절";
		isDisabled = true;
		return {
			step,
			stepDetail,
			isDisabled,
		} as const;
	}

	if (
		expertDataItem.dateOut &&
		expertDataItem.proposalId &&
		expertDataItem.dateRequestConfirmProposal
	) {
		step = ExpertDataItemStep.제안;
		stepDetail = "제안 거절";
		isDisabled = true;

		return {
			step,
			stepDetail,
			isDisabled,
		} as const;
	}

	const now = moment();

	if (expertDataItem.dateTestsession) {
		step = ExpertDataItemStep.인터뷰;

		if (now.isBefore(moment(expertDataItem.dateTestsession))) {
			if (now.isSame(moment(expertDataItem.dateTestsession), "day")) {
				stepDetail = "인터뷰 당일";
			} else {
				stepDetail = `인터뷰 D-${moment(expertDataItem.dateTestsession).diff(now, "days")}`;
			}
		}

		if (now.isAfter(moment(expertDataItem.dateTestsession))) {
			step = ExpertDataItemStep.제안;
			stepDetail = "제안 작성 예정";
		}
	}

	if (expertDataItem.dateRequestForProposal) {
		step = ExpertDataItemStep.제안;
		stepDetail = "제안 작성 예정";
	}

	if (expertDataItem.dateRequestConfirmProposal && expertDataItem.proposalId) {
		step = ExpertDataItemStep.제안;
		stepDetail = "제안 검토";
	}

	if (match.dateStartMatch) {
		stepDetail = "서비스 진행중";
	}

	if (match.dateStartMatch && (!match.isCompleteContract || !match.isCompleteFirstInvoice)) {
		if (match.isCompleteContract && !match.isCompleteFirstInvoice) {
			stepDetail = "계약 및 결제 진행중";
		} else if (!match.isCompleteContract && match.isCompleteFirstInvoice) {
			stepDetail = "계약 진행중";
		} else if (!match.isCompleteContract && !match.isCompleteFirstInvoice) {
			stepDetail = "계약 및 결제 진행중";
		}
	}

	return {
		step,
		stepDetail,
		isDisabled,
	} as const;
};

export const getStage = (matchData: MyMatch, user: User) => {
	let step = AppliCationStep.APPLICATION;

	if (matchData.dateConsulting) {
		step = AppliCationStep.APPLICATION;
	}

	if (matchData.dateConsulting) {
		const conDate = moment(matchData.dateConsulting).set("hours", 19);

		step = AppliCationStep.APPLICATION;
	}

	if (
		user.userType === 2 &&
		matchData.expertDataList.some(
			(data) => data.dateRecommendToExpert || data.dateApplyInterview,
		)
	) {
		step = AppliCationStep.CONSULTING;
	}

	if (matchData.expertDataList.some((data) => data.dateRecommend) && user.userType === 1) {
		step = AppliCationStep.CONSULTING;
	}

	if (
		matchData.expertDataList.some((data) => data.dateRequestTestSession) &&
		user.userType === 2
	) {
		step = AppliCationStep.TEST;
	}

	if (
		matchData.expertDataList.some((data) => data.dateRequestConfirmTestsession) &&
		user.userType === 1
	) {
		step = AppliCationStep.TEST;
	}

	if (matchData.expertDataList.some((data) => data.dateTestsession)) {
		const testDate = moment(matchData.expertDataList.map((data) => data.dateTestsession)[0]);
		step = AppliCationStep.TEST;
		if (testDate.isBefore(moment())) {
			step = AppliCationStep.PROPOSAL;
		}
	}

	if (matchData.expertDataList.some((data) => data.proposalId)) {
		step = AppliCationStep.PROPOSAL;
	}
	return step;
};

export interface FaqData {
	title: string;
	answer: string;
}

const FaqList1 = [
	{
		title: "매칭은 얼마나 걸리나요?",
		answer: "사안에 따라 다르지만 평균적으로 영업일 기준 3일 내에 최적 전문가를 추천해드리며, 7일 내에 원하는 전문가와 협업을 시작할 수 있습니다.",
	},
	{
		title: "어떤 전문가와 일할 수 있나요?",
		answer: "마케팅과 관련된 모든 전문가를 보유하고 있습니다. 세부적으로는 퍼포먼스 마케터 / 콘텐츠 마케터 / 브랜드 마케터 / CRM 마케터 / 디자이너 / 카피라이터가 있습니다.",
	},
	{
		title: "어떤 업무를 요청할 수 있을까요?",
		answer: "전문가와 사전에 협의된 예상 시간/업무 범위 내에서라면 어떤 업무든 요청 가능합니다. 원포인트는 전문가 '고용' 서비스 입니다. 풀타임 정규직 팀원에게 요청 주시는 것과 동일한 방식으로 협업하시면 됩니다.",
	},
	{
		title: "서비스 비용은 어떻게 책정되나요?",
		answer: "전문가가 직접 4주 기준 인건비를 제안하며, 이력 및 역량 수준과 업무 난이도를 고려하여 책정하게 됩니다. 정확한 비용은 전문가 제안 시 알 수 있지만, 평균 단가는 상담 시 안내해드립니다. 전문가가 제시한 인건비 외에 추가 비용은 없습니다.",
	},
	{
		title: "중간에 주당 활용 시간을 변경할 수 있나요?",
		answer: "네, 주당 활용 시간은 5시간부터 40시간까지 자유롭게 변경 가능합니다. 단, 계약 단위에 맞춰 4주 단위로 가능하며, 실제 업무 수행 시간이 계약 시간보다 자주 초과하는 경우에 한해서 4주 내에 상향 조정이 가능합니다. 모든 의사 결정은 고객사 - 전문가 합의 하에 진행됩니다.",
	},
	{
		title: "전문가와 업무는 어떻게 하나요?",
		answer: "대부분의 경우 비대면 형태로 업무하지만, 전문가와의 협의 하에 대면, 비대면, 혼합 (대면+비대면) 등 다양한 형태로 진행 가능합니다.",
	},
];

const FaqList2: FaqData[] = [];

const FaqList3: FaqData[] = [];
const FaqList4: FaqData[] = [];
const FaqList5: FaqData[] = [];
const FaqList6: FaqData[] = [];

const expertFaqList1: FaqData[] = [
	{
		title: "현재 재직중인데 전문가로 활동할 수 있을까요?",

		answer: "가능합니다. 실제로 대다수의 원포인트 전문가분들이 현직자로서 원포인트 서비스를 수행하고 계십니다. 단, 현 직장에서 정해진 근무 시간이 있는 경우 해당 시간을 반드시 피해서 활동하셔야 합니다.",
	},
	{
		title: "재직중인 혹은 재직했던 회사 이력을 마스킹 처리할 수 있을까요?",

		answer: "특정 이력에 대한 마스킹이 필요한 경우 프로필 작성 시 아래와 같이 입력할 수 있습니다.\n예) 롸잇 → 탑티어 마케터 단기 고용 서비스 O사\n단, 고객사는 전문가님의 프로필을 토대로 의사결정을 하기 때문에 마스킹을 하지 않거나 최소한으로 처리하시는 것을 추천드립니다.",
	},
	{
		title: "어떤 직무로 전문가 지원이 가능한가요?",

		answer: "마케팅과 관련된 직무는 어떤 직무든 가능합니다. 퍼포먼스 마케터, 콘텐츠 마케터, 브랜드 마케터, CRM 마케터, 디자이너, 카피라이터가 대표적인 예입니다. ",
	},
	{
		title: "근무는 어떤 형태로 진행되나요?",

		answer: "대부분의 경우 비대면 형태로 업무하지만, 고객사 측에서 희망하는 경우 전문가와의 협의 하에 대면 또는 혼합 (대면+비대면) 근무로 진행됩니다. 업무 형태는 고객사의 업무 공고에서 확인할 수 있습니다.",
	},
	{
		title: "고객사와의 매칭은 어떻게 진행되나요?",

		answer: "다양한 고객사의 업무 공고를 확인하고, 내가 원하는 곳에 원하는 만큼 지원할 수 있습니다. 사전에 프로필을 작성해두시면 빠르고 쉽게 지원할 수 있습니다.",
	},
	{
		title: "매칭 이후 서비스 시작까지 어떤 절차를 거치나요?",

		answer: "고객사와의 Fit 검증을 위한 인터뷰 이후, 진행 의사가 있을시 제안서를 작성해주시면 고객사가 내용을 검토합니다. 고객사 최종 의사 결정까지는 보통 인터뷰 진행 후 영업일 기준 5일 정도 소요됩니다.",
	},
];

export const faqListClient: FaqData[][] = [
	FaqList1,
	FaqList2,
	FaqList3,
	FaqList4,
	FaqList5,
	FaqList6,
];

export const faqListExpert: FaqData[][] = [
	expertFaqList1,
	FaqList2,
	FaqList3,
	FaqList4,
	FaqList5,
	FaqList6,
];
